import { DateTime } from 'luxon';
import { dateOrNextBusinessDay } from '~/components/utils/date';
import type { RequestFormData } from '~/routes/$lang.request.$officeId';
import type { CoreOfficeDataType } from '~/types/offices';

export const buildSearchParams = (formData: RequestFormData, url: string) => {
  const searchParams = new URL(url).searchParams;
  if (formData.email) {
    searchParams.set('email', formData.email);
  }
  searchParams.set('appointments', formData.appointments.join(','));
  searchParams.set('moveinDate', formData.moveinDate);
  searchParams.set('moveoutDate', formData.moveoutDate);
  searchParams.set('requiredCapacity', formData.requiredCapacity);
  searchParams.set('daysPerWeek', formData.daysPerWeek);
  return searchParams;
};

const findStartAndEndDate = (office: CoreOfficeDataType): { startDate: DateTime; endDate: DateTime } => {
  // Calculate start date
  const thirtyDaysFromNow = dateOrNextBusinessDay(DateTime.now().plus({ days: 30 }));
  const earliestStartDate = DateTime.fromISO(office.earliestStartDate);

  const calculatedStartDate = DateTime.max(earliestStartDate, thirtyDaysFromNow);
  let startDate = calculatedStartDate;

  // Calculate end date
  const tenMonthsFromStart = dateOrNextBusinessDay(startDate.plus({ months: 10 }).minus({ days: 1 }));
  const minDurationFromStart = dateOrNextBusinessDay(startDate.plus({ months: office.minDuration }).minus({ days: 1 }));
  const calculatedEndDate = DateTime.max(minDurationFromStart, tenMonthsFromStart);
  let endDate = calculatedEndDate;

  //check if office has a lasAvailabilityDate
  if (office.lastAvailabilityDate) {
    const lastAvailabilityDate = DateTime.fromISO(office.lastAvailabilityDate);
    endDate = DateTime.min(lastAvailabilityDate, calculatedEndDate);
  }

  //check if start- and end-date respect min-duration. If not, adapt start-date
  if (endDate.diff(startDate, 'months').months < office.minDuration) {
    startDate = endDate.minus({ months: office.minDuration }).plus({ days: 1 });
  }

  //set to middle of day to prevent localisation issues when converting to JSDate later
  //needs to be handled properly when localising the whole project

  startDate = startDate.set({ hour: 12 });
  endDate = endDate.set({ hour: 12 });

  return { startDate, endDate };
};

export const findCapacity = (office: CoreOfficeDataType): number => {
  if (!office.minCapacity) return office.capacity;
  if (office.minCapacity <= 8 && office.capacity >= 8) return 8;
  return [office.minCapacity, office.capacity].reduce((prev, curr) =>
    Math.abs(8 - prev) < Math.abs(8 - curr) ? prev : curr,
  );
};

export const initializeRequestData = (office: CoreOfficeDataType, searchParams: URLSearchParams): string => {
  if (!searchParams.has('daysPerWeek')) {
    const daysPerWeek = office.highestFlexibility;
    searchParams.append('daysPerWeek', daysPerWeek.toString());
  }

  if (!searchParams.has('requiredCapacity')) {
    const requiredCapacity = findCapacity(office);
    searchParams.append('requiredCapacity', requiredCapacity.toString());
  }
  const { startDate, endDate } = findStartAndEndDate(office);
  if (!searchParams.has('moveinDate')) {
    searchParams.append('moveinDate', startDate.toString());
  }

  if (!searchParams.has('moveoutDate')) {
    searchParams.append('moveoutDate', endDate.toString());
  }
  return searchParams.toString();
};

export const validateRequestParams = (searchParams: URLSearchParams): boolean => {
  const requestParamsValid =
    searchParams.has('daysPerWeek') &&
    searchParams.has('requiredCapacity') &&
    searchParams.has('moveinDate') &&
    searchParams.has('moveoutDate') &&
    searchParams.has('appointments');
  return requestParamsValid;
};

export const requestStepperSteps = [
  { labelKey: 'contactRequest.stepper.signup.label', position: 1 },
  { labelKey: 'contactRequest.stepper.company.label', position: 2 },
  { labelKey: 'contactRequest.stepper.message.label', position: 3 },
];
